// Moldura do backoffice: barra lateral (só as secções a que se tem acesso) + barra de topo com a conta.
import { useAuthLoader, useMe, useRefreshMe } from "@ecliptica/api-client/react";
import { activeText, LanguageSwitch, useTranslation } from "@ecliptica/i18n";
import { Avatar, Badge, cn, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@ecliptica/ui";
import { Logo } from "@ecliptica/game-ui";
import { LogOut } from "lucide-react";
import { NavLink, Outlet, useNavigate } from "react-router";
import { useCan } from "../features/auth/RequireStaff";
import { SECTIONS } from "../features/nav/sections";

export function AppLayout() {
  const { t } = useTranslation();
  const can = useCan();
  const sections = SECTIONS.filter((s) => can(s.permission));
  return (
    <div className="flex min-h-dvh">
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r bg-night-2/60 md:flex">
        <div className="flex h-14 items-center gap-2 border-b px-5">
          <Logo mono className="text-[11px]" />
          <Badge variant="secondary" className="text-[10px]">
            {t("brand.tag")}
          </Badge>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 p-3">
          {sections.map(({ key, path, icon: Icon, milestone }) => (
            <NavLink
              key={key}
              to={path}
              end={path === "/"}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
                  isActive && "bg-accent text-gold-bright",
                )
              }
            >
              <Icon className="size-4" />
              <span className="flex-1">{t(`nav.${key}`)}</span>
              {milestone && <span className="text-[10px] uppercase tracking-wider opacity-60">{milestone}</span>}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b bg-background/90 px-4 backdrop-blur sm:px-6">
          <Logo mono className="text-[10px] md:hidden" />
          <div className="ml-auto flex items-center gap-3">
            <LanguageSwitch />
            <AccountMenu />
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function AccountMenu() {
  const { t, i18n } = useTranslation();
  const me = useMe();
  const loadAuth = useAuthLoader();
  const refreshMe = useRefreshMe();
  const navigate = useNavigate();
  const u = me.data?.user;
  if (!u) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <Avatar name={u.name} src={u.image ?? undefined} className="size-8" />
        <span className="hidden text-left text-sm leading-tight sm:block">
          {u.name}
          <span className="block text-[11px] text-muted-foreground">{activeText(u.roleLabels, i18n.language) ?? u.role}</span>
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{u.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={async () => {
            await (await loadAuth()).signOut();
            await refreshMe();
            navigate("/entrar");
          }}
        >
          <LogOut /> {t("auth.signOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
